<template>
  <div :class="size === 'sm' ? 'h-8' : ' h-10'">
    <div
      :class="propsClass"
      class="h-full"
      ref="inputRef"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
      @click="handleClick">
      <span
        class="absolute inset-y-0 start-2 grid place-content-center max-w-[36px] pr-6"
        v-if="icon === 'left'">
        <slot name="icon-left"></slot>
      </span>
      <slot name="prefix"> </slot>
      <input
        ref="fieldRef"
        v-model.trim="data"
        class="h-full"
        :class="inputClass"
        :placeholder="placeholder"
        :type="passwordFieldType"
        lang="en"
        :min="min"
        :max="max"
        :readonly="readonly"
        :disabled="mode === 'view' || disabled"
        @change="emit('changed')"
        @focus="focused = true"
        @blur="onBlur"
        @keydown.enter="emit('enterKeydown')"
        @input="debouncedUpdateSearch" />

      <slot name="suffix"></slot>
      <button
        v-if="showPasswordToggle && type === 'password'"
        type="button"
        @click="togglePasswordVisibility"
        class="absolute inset-y-0 grid pr-2 end-0 place-content-center"
        :disabled="disabled">
        <svg
          v-if="isPasswordVisible"
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5 text-gray-500 hover:text-gray-700"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5 text-gray-500 hover:text-gray-700"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </button>
      <span class="absolute inset-y-0 grid pl-6 end-0 place-content-center" v-if="icon === 'right'">
        <slot name="icon-right"></slot>
      </span>
    </div>
    <p class="mx-2 -mt-0.5 text-xs text-red-500" v-show="error && showErrorAsTooltip == false">
      {{ error }}
    </p>
    <p
      class="mx-2 -mt-0.5 text-xs text-orange-500"
      v-show="!error && warning && showErrorAsTooltip == false">
      {{ warning }}
    </p>
    <Teleport to="body">
      <div
        v-if="!!showErrorAsTooltip && tooltipVisible && error && mode === 'edit'"
        ref="tooltipRef"
        :style="{
          position: strategy,
          top: y ? `${y}px` : '',
          left: x ? `${x}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-white bg-red-500 rounded-lg shadow-lg">
        <span class="text-xs truncate">{{ error }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-red-500 fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
      <div
        v-if="!!showErrorAsTooltip && tooltipVisible && !error && warning && mode === 'edit'"
        ref="tooltipRef"
        :style="{
          position: strategy,
          top: y ? `${y}px` : '',
          left: x ? `${x}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-white bg-orange-500 rounded-lg shadow-lg">
        <span class="text-xs truncate">{{ warning }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-orange-500 fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { computed, nextTick, ref, watch, watchEffect } from 'vue';
import { useFloating, autoUpdate, offset, flip, shift, hide } from '@floating-ui/vue';
import { useDebounceFn } from '@vueuse/core';

type BaseInputProps = {
  type?: 'text' | 'password' | 'email' | 'search' | 'number';
  placeholder?: string;
  icon?: 'left' | 'right' | null;
  disabled?: boolean;
  color?: 'base' | 'theme' | 'light' | 'dark' | 'green' | 'red' | 'purple';
  error?: string;
  warning?: string;
  showErrorAsTooltip?: boolean;
  prefix?: boolean;
  flat?: boolean;
  mode?: 'view' | 'edit';
  readonly?: boolean;
  min?: number;
  max?: number;
  size?: 'sm' | 'default';
  textRight?: boolean;
  price?: boolean;
  lineFocusInput?: boolean;
  showPasswordToggle?: boolean;
};

const data = defineModel<string | number | undefined>();

const props = withDefaults(defineProps<BaseInputProps>(), {
  type: 'text',
  color: 'theme',
  mode: 'edit',
  showErrorAsTooltip: false,
  size: 'default',
  showPasswordToggle: false,
});

const propsClass = computed(() => {
  return cva(
    'relative inline-block rounded-lg focus:outline-none focus:shadow-none w-full flex h-full px-1.5',
    {
      variants: {
        prefix: {
          true: '',
          false: '',
        },
        disabled: {
          true: '',
          false: '',
        },
        icon: {
          left: 'pl-10',
          right: 'pr-3',
        },
        error: {
          true: '',
        },
        warning: {
          true: '',
        },
        color: {
          base: '',
          theme: '',
          light: '',
          dark: 'bg-gray-800',
          green: '',
          red: '',
          purple: '',
        },
        focused: {
          true: '',
        },
        flat: {
          true: '',
          false: 'shadow-sm',
        },
        mode: {
          view: '',
          edit: 'border',
        },
      },
      compoundVariants: [
        // Focused states
        { focused: true, error: true, class: 'border-theme-500 border-2' },
        { focused: true, error: false, warning: true, class: 'border-theme-500 border-2' },
        { focused: true, error: false, warning: false, class: 'border-theme-500/50' },

        // Error states (not focused)
        { focused: false, error: true, class: 'border-red-500 text-red-500' },
        { focused: false, error: false, warning: true, class: 'border-orange-500 text-orange-500' },

        // Color borders and text when no error AND no warning AND not focused
        { focused: false, error: false, warning: false, color: 'base', class: 'border-gray-300' },
        {
          focused: false,
          error: false,
          warning: false,
          color: 'theme',
          class: 'border-theme-700 text-theme-700',
        },
        {
          focused: false,
          error: false,
          warning: false,
          color: 'dark',
          class: 'border-gray-800 text-white',
        },
        {
          focused: false,
          error: false,
          warning: false,
          color: 'green',
          class: 'border-green-700 text-green-700',
        },
        {
          focused: false,
          error: false,
          warning: false,
          color: 'red',
          class: 'border-red-700 text-red-700',
        },
        {
          focused: false,
          error: false,
          warning: false,
          color: 'purple',
          class: 'border-purple-700 text-purple-700',
        },

        // Mode and prefix
        { mode: 'edit', prefix: true, class: 'pr-1' },
        { mode: 'edit', prefix: false, class: 'px-0' },
        {
          mode: 'edit',
          disabled: true,
          class: 'bg-opacity-75 text-gray-500 bg-gray-100 border border-gray-300',
        },
        { mode: 'edit', disabled: false, class: 'bg-opacity-1' },
        { mode: 'view', disabled: false, class: '' },

        // Text color for base in edit mode
        { mode: 'edit', color: 'base', class: 'text-gray-700' },
      ],
    }
  )({
    disabled: props.disabled,
    icon: props.icon,
    color: props.color,
    error: !!props.error,
    warning: !!props.warning,
    focused: focused.value,
    prefix: props.prefix,
    mode: props.mode,
  });
});
const inputClass = computed(() => {
  return cva('bg-transparent outline-none overflow-hidden w-full', {
    variants: {
      color: {
        base: '',
        theme: '',
        light: '',
        dark: 'placeholder:text-white',
        green: '',
        red: '',
        purple: '',
      },
      mode: {
        view: '',
        edit: 'px-0',
      },
      price: {
        true: 'text-right',
        false: '',
      },
      iconRight: {
        true: 'pr-1',
      },
    },
  })({
    color: props.color,
    mode: props.mode,
    price: props.price || props.textRight,
    iconRight: props.icon && props.type == 'number',
  });
});

const focused = ref(false);
const isPasswordVisible = ref(false);

const passwordFieldType = computed(() => {
  if (props.type === 'password' && isPasswordVisible.value) {
    return 'text';
  }
  return props.type;
});

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value;
}

const emit = defineEmits(['blur', 'changed', 'debouncedInputChanged', 'enterKeydown']);

function onBlur() {
  focused.value = false;
  if (props.price) formatDecimals(data.value as number);
  emit('blur');
}

const debouncedUpdateSearch = useDebounceFn(
  () => {
    emit('debouncedInputChanged');
  },
  500,
  { maxWait: 800 }
);

const inputRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);

const { x, y, strategy, update, middlewareData } = useFloating(inputRef, tooltipRef, {
  placement: 'top',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, update) => {
    return autoUpdate(referenceEl, floatingEl, update);
  },
});

function showTooltip() {
  tooltipVisible.value = true;
  update();
}

function hideTooltip() {
  tooltipVisible.value = false;
}

function handleClick(event: Event) {
  if (props.disabled || props.mode === 'view') {
    event.preventDefault();
    event.stopPropagation();
  }
}

watchEffect(() => {
  if (middlewareData.value.hide?.referenceHidden) {
    tooltipVisible.value = false;
  }
});

const fieldRef = ref<HTMLInputElement | null>(null);
function focusInput() {
  fieldRef.value?.focus();
}
defineExpose({
  focus: focusInput,
});

watch(
  () => data.value,
  (newVal) => {
    if (!props.price) return;
    nextTick(() => formatDecimals(newVal as number));
  },
  { immediate: true }
);

function formatDecimals(value: number) {
  if (!props.price) return;
  const el = fieldRef.value;
  if (!el || el === document.activeElement) return;
  const num = Number(value);
  if (!Number.isFinite(num)) return;
  el.value = num.toFixed(2);
}

watch(
  () => props.lineFocusInput,
  (val) => {
    if (!val) return;
    nextTick(() => {
      fieldRef.value?.focus();
    });
  },
  {
    flush: 'post',
    immediate: true,
  }
);
</script>
